import { useAccess } from '@/common/hooks/useAccess';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import React, { useContext, useEffect, useState } from 'react';
import {
  canInitReconsideration,
  isAdmin,
  isManager,
} from '../../common/actionHelper';
import { FlexCol, FlexRow, Spacer, TitleSmall } from '../../common/components/v2';
import { FeatureFlagsContext } from '../../common/featureFlagsWrapper';
import { HttpService } from '../../common/http.service';
import {
  getReconsiderationStatusEndPoint,
  initiateReconsiderationEndPoint,
  updateHipaaRevocationEndPoint,
} from './api.endPoints';
import { PolicyData } from './dtos/policyData.dto';
import { MoveToWorkbench } from './MoveToWorkbench';
import styles from './styles.module.scss';

const initiateReconsideration = async (
  carrier: string,
  product: string,
  policyId: number,
): Promise<any> => {
  try {
    const url = initiateReconsiderationEndPoint(carrier, product, policyId);
    const data = await HttpService.getInstance().post(url);
    if (data && !data.error && Object.keys(data.parsedBody).length !== 0) {
      return data.parsedBody;
    }

    throw new Error(data?.error || 'Failed to initiate reconsideration');
  } catch (error) {
    console.error('Error initiating reconsideration:', error);
    throw error;
  }
};

const updateHipaaRevocation = async (
  policyId: number,
  isHipaaRevoked: boolean,
): Promise<any> => {
  try {
    const url = updateHipaaRevocationEndPoint(policyId);
    const data = await HttpService.getInstance().patch(url, {
      revoked: isHipaaRevoked,
    });
    if (data && !data.error && Object.keys(data.parsedBody).length !== 0) {
      return;
    }

    throw new Error(data?.error || 'Failed to update hipaa revocation');
  } catch (error) {
    console.error('Error update hipaa revocation:', error);
    throw error;
  }
};

const getReconsiderationStatus = async (
  carrier: string,
  product: string,
  policyId: number,
): Promise<boolean> => {
  try {
    const url = getReconsiderationStatusEndPoint(carrier, product, policyId);
    const data = await HttpService.getInstance().get(url);
    if (data && !data.error) {
      return data.parsedBody?.isReconsiderationProfile || false;
    }
    throw new Error(data?.error || 'Failed to get reconsideration status');
  } catch (error) {
    console.error('Error checking reconsideration status:', error);
    throw error;
  }
};

interface ActionsProps {
  policyData: PolicyData;
}

export const Actions: React.FC<ActionsProps> = ({ policyData }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUnderReconsideration, setIsUnderReconsideration] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const canInitReconsiderationAction = useAccess({
    permission: 'INITIATE_RECONSIDERATION',
    legacyCheck: canInitReconsideration,
  });

  const canAccessHIPAASection = useAccess({
    permission: 'UPDATE_HIPAA_REVOCATION',
    legacyCheck: roles => isAdmin(roles) || isManager(roles),
  });

  const canMigrateToWorkbench = useAccess({
    permission: 'MIGRATE_TO_WORKBENCH',
    legacyCheck: isAdmin,
  });

  const { flags } = useContext(FeatureFlagsContext);

  const [isHipaaRevoked, setIsHipaaRevoked] = useState(
    policyData.meta?.hipaaRevoked ?? false,
  );

  useEffect(() => {
    const checkReconsiderationStatus = async () => {
      try {
        setIsLoadingStatus(true);
        setStatusError(null);
        const status = await getReconsiderationStatus(
          policyData.carrier,
          policyData.product,
          policyData.policyId,
        );
        setIsUnderReconsideration(status);
        setStatusError(null);
      } catch (err) {
        console.error('Failed to check reconsideration status:', err);
        setStatusError('Unable to check reconsideration status');
        setIsUnderReconsideration(false);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    checkReconsiderationStatus();
  }, [policyData.policyId]);

  const handleHipaaRevocation = async () => {
    try {
      await updateHipaaRevocation(policyData.policyId, !isHipaaRevoked);
      setIsHipaaRevoked(!isHipaaRevoked);
    } catch (error) {
      console.error('Error updating hipaa revocation:', error);
      setError(
        'Failed to update hipaa revocation. Please inform the engineers if issue persists.',
      );
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setName('');
    setIsValid(false);
    setError(null);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputName = event.target.value;
    setName(inputName);
    // Check if name matches policy holder's full name
    const fullName = `${policyData.firstName} ${policyData.lastName}`;
    setIsValid(inputName === fullName);
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await initiateReconsideration(
        policyData.carrier,
        policyData.product,
        policyData.policyId,
      );

      // Update reconsideration status based on the API response
      setIsUnderReconsideration(response.isReconsiderationProfile || false);

      // Close dialog after submission
      handleClose();
    } catch (error) {
      console.error('Error initiating reconsideration:', error);
      setError(
        'Failed to initiate reconsideration. Please inform the engineers if issue persists.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (
    !canInitReconsiderationAction &&
    !canAccessHIPAASection &&
    !canMigrateToWorkbench
  ) {
    return null;
  }

  return (
    <Paper
      className={styles.sectionContainer}
      elevation={0}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <FlexRow className={styles.Title}>
        <TitleSmall>{'Actions'}</TitleSmall>
      </FlexRow>
      <Box
        mt={2}
        className={styles.actionContainer}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        {flags.reconsiderationEnabled && canInitReconsiderationAction && (
          <FlexRow>
            <FlexCol sm={3} lg={3} className={styles.Label}>
              Reconsideration
            </FlexCol>
            <FlexCol sm={9} lg={9} className={styles.Info}>
              {isLoadingStatus ? (
                'Loading status...'
              ) : statusError ? (
                <span style={{ color: 'red' }}>{statusError}</span>
              ) : isUnderReconsideration ? (
                'Applicant is already under Reconsideration'
              ) : (
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClickOpen}
                  >
                    Initiate Reconsideration
                  </Button>
                </span>
              )}
            </FlexCol>
          </FlexRow>
        )}
        {canAccessHIPAASection && (
          <>
          <Spacer height={1} />
          <FlexRow>
            <FlexCol sm={3} lg={3} className={styles.Label}>
              HIPAA Revocation
            </FlexCol>
            <FlexCol sm={9} lg={9} className={styles.Info}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleHipaaRevocation}
                >
                  {isHipaaRevoked ? 'Restore HIPAA' : 'Revoke HIPAA'}
                </Button>
              </span>
            </FlexCol>
          </FlexRow>
          </>
        )}
        {canMigrateToWorkbench && (
          <>
          <Spacer height={1} />
          <FlexRow>
            <FlexCol sm={3} lg={3} className={styles.Label}>
              Workbench Migration
            </FlexCol>
            <FlexCol sm={9} lg={9} className={styles.Info}>
              <MoveToWorkbench policyData={policyData} />
            </FlexCol>
          </FlexRow>
          </>
        )}
      </Box>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Confirm Reconsideration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the policy holder's name to confirm reconsideration
            initiation.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Policy Holder Name"
            type="text"
            fullWidth
            variant="outlined"
            value={name}
            onChange={handleNameChange}
            error={!!error}
            helperText={
              error ||
              '[FirstName] [LastName] only, ignore [MiddleName]. e.g. John Doe'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            color="primary"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
